import { useDeleteOrganizationMembership } from '@modules/organizations/api/useDeleteOrganizationMembership';
import { useGetOrganizationOwners } from '@modules/organizations/api/useGetOrganizationOwners';
import * as Styled from '@modules/organizations/pages/organizations-list/components/OrganizationBox/ConfirmDelete/ConfirmDelete.styled';
import { FetchError } from '@utils/FetchError';
import { AbsoluteSpinner } from 'app/_components/AbsoluteSpinner';

import { Toast } from '@capra/core';
import { Input, Modal, Typography } from 'antd';
import * as React from 'react';
import { OrgRole } from 'shared/src/authz/rolesV2';

const { Text } = Typography;

interface IConfirmDeleteProps {
  organizationId: string;
  role: OrgRole;
  organizationAlias?: string;
  fetchMemberships: () => Promise<unknown>;
}

export const ConfirmDelete = ({
  organizationId,
  role,
  organizationAlias,
  fetchMemberships,
}: IConfirmDeleteProps) => {
  const [isDeleteEnabled, setDeleteEnable] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [canLeave, setCanLeave] = React.useState<undefined | boolean>();
  const { trigger: triggerDeleteOrganizationMembership, isMutating } = useDeleteOrganizationMembership();
  const { mutate } = useGetOrganizationOwners(organizationId);

  React.useEffect(() => {
    if (!showModal || canLeave !== undefined) {
      return;
    }

    const checkIfCanLeave = async () => {
      if (role === OrgRole.OWNER) {
        const owners = await mutate();

        const minOwners = 1;

        setCanLeave((owners && owners?.length > minOwners) || false);
      } else {
        setCanLeave(true);
      }
    };

    checkIfCanLeave();
  }, [showModal]);

  const handleDelete = async (): Promise<unknown> => {
    if (isMutating) {
      return;
    }

    try {
      await triggerDeleteOrganizationMembership({ organizationId });
      await fetchMemberships();
      Toast.success(`You have successfully left ${organizationAlias || organizationId}.`, { position: 'bottom-right' });
    } catch (e: unknown) {
      if (e instanceof FetchError) {
        Toast.error('Unable to leave organization. Please contact saas-support@cribl.io.', { position: 'bottom-right' });
      } else {
        Toast.error((e as Error).message || 'An error occurred while trying to leave organization.', { position: 'bottom-right' });
      }
    }
  };

  const handleLeaveClick = (): void => {
    setShowModal(true);
  };

  return (
    <>
      <Styled.ConfirmDeleteButton
        onClick={handleLeaveClick}
        data-testid={`leave-org-${organizationId}`}
      >
        Leave Organization
      </Styled.ConfirmDeleteButton>

      {showModal && (
        <div
          role='presentation'
        >
          <Modal
            cancelText='Cancel'
            onOk={handleDelete}
            okButtonProps={{ disabled: !isDeleteEnabled }}
            okText='Leave'
            onCancel={(): void => setShowModal(false)}
            open
            title={canLeave === false ? 'Error' : `Leave ${organizationId}?`}
          >
            {canLeave === false && (
              <>
                <p>
                  To leave <Text code>{organizationId}</Text>, first add a new Owner.
                </p>

                <p>
                  To retire <Text code>{organizationId}</Text>, click{' '}
                  <Text strong>Account &#8594; Organization &#8594; Delete Organization</Text>.
                </p>
              </>
            )}

            {canLeave === true && (
              <>
                <p>You won&apos;t be able to re-join this Organization with out a new invitation.</p>

                <label htmlFor='organization'>
                  Type <Text code>{organizationId}</Text> below to confirm.
                  <Input
                    name='organization'
                    type='text'
                    // Needed for some keyboard interactions to work inside modal + menuitem https://github.com/ant-design/ant-design/issues/34125
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e): void => {
                      setDeleteEnable(e.target.value === organizationId);
                    }}
                  />
                </label>
              </>
            )}

            {canLeave === undefined && (
              <p>
                <AbsoluteSpinner />
              </p>
            )}
          </Modal>
        </div>
      )}
    </>
  );
};
